import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  GripVertical,
  Camera,
  MapPin,
  Wifi,
  Wand2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDefaultFunctions,
  useDefaultFunctionGroups,
  useCreateDefaultFunctionGroup,
  useCreateDefaultFunction,
  useUpdateDefaultFunction,
  useDeleteDefaultFunction,
} from "@/hooks/useDefaultFunctions";
import { useAppSetting, useUpdateAppSetting } from "@/hooks/useAppSettings";

export interface InitializeFunctionsConfig {
  cameraOperatorFunctionId: string | null;
  droneOperatorFunctionId: string | null;
  hiddenFunctionIds: string[];
}

const SETTING_KEY = "initialize_functions_config";

export default function DefaultFunctionsPage() {
  const { data: functions, isLoading } = useDefaultFunctions();
  const { data: functionGroups = [] } = useDefaultFunctionGroups();
  const createFunctionGroup = useCreateDefaultFunctionGroup();
  const createFunction = useCreateDefaultFunction();
  const updateFunction = useUpdateDefaultFunction();
  const deleteFunction = useDeleteDefaultFunction();

  const { data: initConfig } =
    useAppSetting<InitializeFunctionsConfig>(SETTING_KEY);
  const updateSetting = useUpdateAppSetting();

  const [newName, setNewName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupId, setNewGroupId] = useState<string | null>(null);
  const [newShowOnInit, setNewShowOnInit] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const config: InitializeFunctionsConfig = initConfig ?? {
    cameraOperatorFunctionId: null,
    droneOperatorFunctionId: null,
    hiddenFunctionIds: [],
  };

  const saveConfig = (updates: Partial<InitializeFunctionsConfig>) => {
    updateSetting.mutate({
      key: SETTING_KEY,
      value: { ...config, ...updates },
    });
  };

  const toggleInitVisibility = (fnId: string, currentValue: boolean) => {
    updateFunction.mutate({
      id: fnId,
      updates: { show_on_initialize: !currentValue },
    });
  };

  const handleCreate = () => {
    if (newName.trim()) {
      const maxOrder =
        functions?.reduce((max, f) => Math.max(max, f.display_order), -1) ?? -1;
      createFunction.mutate({
        name: newName.trim(),
        default_function_group_id: newGroupId,
        display_order: maxOrder + 1,
        show_on_initialize: newShowOnInit,
      });
      setNewName("");
      setNewGroupId(null);
      setNewShowOnInit(true);
    }
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    const maxOrder = functionGroups.reduce(
      (max, group) => Math.max(max, group.display_order),
      -1,
    );
    createFunctionGroup.mutate({
      name: newGroupName.trim(),
      display_order: maxOrder + 1,
    });
    setNewGroupName("");
  };

  const handleUpdate = (id: string) => {
    if (editingName.trim()) {
      updateFunction.mutate({ id, updates: { name: editingName.trim() } });
      setEditingId(null);
    }
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteFunction.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const startEditing = (id: string, name: string) => {
    setEditingId(id);
    setEditingName(name);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <PageHeader title="Default Functions" />
        <p className="text-sm text-muted-foreground">
          Manage globally available function templates. These are used as
          presets when creating project functions and as the basis for the skill
          matrix.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Functions</CardTitle>
          <CardDescription>
            Add function names that can be reused across projects (e.g., Camera
            Operator, Sound Engineer, Director).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new function */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row">
              <Input
                placeholder="New group name..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateGroup();
                }}
              />
              <Button
                variant="outline"
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim() || createFunctionGroup.isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Group
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="New function name..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
              />
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || createFunction.isPending}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
            <Select
              value={newGroupId ?? "none"}
              onValueChange={(value) =>
                setNewGroupId(value === "none" ? null : value)
              }
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="No group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No group</SelectItem>
                {functionGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={newShowOnInit}
                onCheckedChange={(checked) => setNewShowOnInit(!!checked)}
              />
              <span className="text-sm text-muted-foreground">
                Show in initialize dialog
              </span>
            </label>
          </div>

          {/* Function list */}
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : functions?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No default functions yet. Add your first one above.
            </div>
          ) : (
            <div className="space-y-1">
              {functions?.map((fn) => {
                const isHidden = !fn.show_on_initialize;
                return (
                  <div
                    key={fn.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                    {editingId === fn.id ? (
                      <div className="flex-1 flex items-center gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate(fn.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          className="h-8"
                          autoFocus
                        />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleUpdate(fn.id)}
                          disabled={!editingName.trim()}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 font-medium text-sm flex items-center gap-1.5">
                          {fn.name}
                          {fn.system_tag === "camera_position" && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 py-0"
                            >
                              Camera
                            </Badge>
                          )}
                          {fn.location_type && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 py-0"
                            >
                              {fn.location_type === "remote"
                                ? "Remote"
                                : "On-site"}
                            </Badge>
                          )}
                          {fn.default_function_group && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1 py-0"
                            >
                              {fn.default_function_group.name}
                            </Badge>
                          )}
                        </span>
                        <Select
                          value={fn.default_function_group_id ?? "none"}
                          onValueChange={(value) =>
                            updateFunction.mutate({
                              id: fn.id,
                              updates: {
                                default_function_group_id:
                                  value === "none" ? null : value,
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-[140px]">
                            <SelectValue placeholder="No group" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No group</SelectItem>
                            {functionGroups.map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                {group.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={isHidden ? "ghost" : "secondary"}
                              size="icon-sm"
                              onClick={() =>
                                toggleInitVisibility(
                                  fn.id,
                                  fn.show_on_initialize,
                                )
                              }
                            >
                              <Wand2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isHidden
                              ? "Show in initialize dialog"
                              : "Hide from initialize dialog"}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={
                                fn.system_tag === "camera_position"
                                  ? "secondary"
                                  : "ghost"
                              }
                              size="icon-sm"
                              onClick={() =>
                                updateFunction.mutate({
                                  id: fn.id,
                                  updates: {
                                    system_tag:
                                      fn.system_tag === "camera_position"
                                        ? null
                                        : "camera_position",
                                  },
                                })
                              }
                            >
                              <Camera className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {fn.system_tag === "camera_position"
                              ? "Remove camera position tag"
                              : "Mark as camera position"}
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={fn.location_type ? "secondary" : "ghost"}
                              size="icon-sm"
                              onClick={() => {
                                const next =
                                  fn.location_type === null
                                    ? "on_site"
                                    : fn.location_type === "on_site"
                                      ? "remote"
                                      : null;
                                updateFunction.mutate({
                                  id: fn.id,
                                  updates: { location_type: next },
                                });
                              }}
                            >
                              {fn.location_type === "remote" ? (
                                <Wifi className="h-3.5 w-3.5" />
                              ) : (
                                <MapPin className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {fn.location_type === null
                              ? "Set to on-site"
                              : fn.location_type === "on_site"
                                ? "Set to remote"
                                : "Remove location type"}
                          </TooltipContent>
                        </Tooltip>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => startEditing(fn.id, fn.name)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(fn.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Camera Plan Defaults</CardTitle>
          <CardDescription>
            Select which functions get their default count pre-filled from
            camera plans when initializing project functions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Camera Operator Function</Label>
              <Select
                value={config.cameraOperatorFunctionId ?? "__none__"}
                onValueChange={(val) =>
                  saveConfig({
                    cameraOperatorFunctionId: val === "__none__" ? null : val,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select function..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {functions?.map((fn) => (
                    <SelectItem key={fn.id} value={fn.id}>
                      {fn.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Count of non-drone cameras per day
              </p>
            </div>
            <div className="space-y-2">
              <Label>Drone Operator Function</Label>
              <Select
                value={config.droneOperatorFunctionId ?? "__none__"}
                onValueChange={(val) =>
                  saveConfig({
                    droneOperatorFunctionId: val === "__none__" ? null : val,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select function..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {functions?.map((fn) => (
                    <SelectItem key={fn.id} value={fn.id}>
                      {fn.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Count of drone cameras per day
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Function?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this default function and all
              associated skill ratings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
